'use client';

import * as React from 'react';
import { Select as SelectPrimitive } from 'radix-ui';

import { ChevronDownIcon, CheckIcon, ChevronUpIcon } from 'lucide-react';
import { cn } from './lib/utils';
import { suppressScrollAdjustments } from './lib/suppress-scroll-adjustments';

function Select({ onOpenChange, ...props }: React.ComponentProps<typeof SelectPrimitive.Root>) {
  const restoreRef = React.useRef<(() => void) | null>(null);

  const handleOpenChange = (open: boolean) => {
    if (open) {
      restoreRef.current?.();
      restoreRef.current = suppressScrollAdjustments();
    } else {
      restoreRef.current?.();
      restoreRef.current = null;
    }
    onOpenChange?.(open);
  };

  React.useEffect(() => () => restoreRef.current?.(), []);

  return <SelectPrimitive.Root data-slot="select" onOpenChange={handleOpenChange} {...props} />;
}

// Every Radix SelectContent wraps its children in react-remove-scroll to
// block background scrolling while open. Its scrollbar-compensation half
// (react-remove-scroll-bar) does this by adding `data-scroll-locked` to
// <body>, which an injected stylesheet turns into
// `body[data-scroll-locked] { overflow: hidden !important; ... }`. Setting
// `overflow: hidden` on the element that owns page scroll (body, in this
// app) is a *native* browser action that resets its scroll offset to 0 the
// instant it's applied — no JS scroll/focus call involved, which is why
// intercepting scrollIntoView/focus never touched this. That reset is what
// reads as "the page jumps to the top" the moment any Select opens, for as
// long as it stays open (react-remove-scroll doesn't compensate for this
// itself).
//
// A first version of this fix compensated the way `SbDialog.tsx` does for
// its own overlay — `position: fixed; top: -scrollY` on body, so it has
// nothing to reset. That works for a modal, but a Select isn't one: taking
// body out of flow breaks every `position: sticky` element on the page
// (the site header among them — it lost its scrolling ancestor and
// rendered at its static position, which the `top` offset then pushed off
// the top of the viewport, i.e. it visibly disappeared while any Select
// was open). Simpler and side-effect-free: don't compensate for
// `overflow: hidden` at all, just don't let it apply to body in the first
// place. Background scroll stays fully live while a Select is open, same
// as `SbSelect` already behaves — an accepted trade-off, not a regression.
//
// `!important` is required because react-remove-scroll-bar's rule is also
// `!important`, and inline `!important` is the only thing that outranks an
// external stylesheet `!important` in the cascade.
//
// The lock is armed from the trigger's raw `onPointerDown`/`onKeyDown` (in
// `SelectTrigger`, not here) instead of this component's `onOpenChange`.
// Radix's `Select` is uncontrolled by default, and
// `@radix-ui/react-use-controllable-state`'s uncontrolled path calls
// `onOpenChange` from a plain `useEffect` — which runs *after* React
// commits and the browser paints. react-remove-scroll-bar arms its own
// lock from a layout effect (pre-paint), so it always won that race: by
// the time an onOpenChange-driven fix ran, the collapsed frame had already
// been painted. Arming on the trigger's native event guarantees we're
// first. Released via a MutationObserver watching for `data-scroll-locked`
// to clear, which fires in step with react-remove-scroll-bar's own cleanup
// regardless of our React effect timing.
let scrollLockRestore: (() => void) | null = null;
let scrollLockObserverInstalled = false;

function releaseScrollLockIfUnlocked() {
  if (!document.body.hasAttribute('data-scroll-locked') && scrollLockRestore) {
    scrollLockRestore();
    scrollLockRestore = null;
  }
}

function armScrollLock() {
  if (scrollLockRestore) return; // already locked (rapid re-open, nested selects, ...)
  if (!scrollLockObserverInstalled) {
    scrollLockObserverInstalled = true;
    new MutationObserver(releaseScrollLockIfUnlocked).observe(document.body, {
      attributes: true,
      attributeFilter: ['data-scroll-locked'],
    });
  }
  scrollLockRestore = preventBodyScrollReset();
}

function preventBodyScrollReset(): () => void {
  const bodyEl = document.body;
  const prevOverflow = bodyEl.style.overflow;
  const prevMarginRight = bodyEl.style.marginRight;
  const prevPaddingRight = bodyEl.style.paddingRight;
  bodyEl.style.setProperty('overflow', 'visible', 'important');
  // react-remove-scroll-bar's injected `body[data-scroll-locked]` rule also
  // sets `margin-right`/`padding-right` to the scrollbar's width, to close
  // the gap it expects `overflow: hidden` to leave once the OS scrollbar
  // disappears. Since the line above cancels that `overflow: hidden` (the
  // whole point — see the block comment above `armScrollLock`), the
  // scrollbar never actually goes away, so that compensation has nothing to
  // compensate for: it just shoves body's content left by the scrollbar's
  // width, opening a visible gap on the right instead of closing one.
  // Zeroing both here for the same duration cancels that side effect.
  bodyEl.style.setProperty('margin-right', '0', 'important');
  bodyEl.style.setProperty('padding-right', '0', 'important');
  return () => {
    bodyEl.style.overflow = prevOverflow;
    bodyEl.style.marginRight = prevMarginRight;
    bodyEl.style.paddingRight = prevPaddingRight;
  };
}

function SelectGroup({ className, ...props }: React.ComponentProps<typeof SelectPrimitive.Group>) {
  return (
    <SelectPrimitive.Group
      data-slot="select-group"
      className={cn('scroll-my-1 p-1', className)}
      {...props}
    />
  );
}

function SelectValue({ ...props }: React.ComponentProps<typeof SelectPrimitive.Value>) {
  return <SelectPrimitive.Value data-slot="select-value" {...props} />;
}

const SELECT_OPEN_KEYS = new Set([' ', 'Enter', 'ArrowUp', 'ArrowDown']);

function SelectTrigger({
  className,
  size = 'default',
  children,
  fill = false,
  onPointerDown,
  onKeyDown,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Trigger> & {
  size?: 'sm' | 'default';
  fill?: boolean;
}) {
  // See `armScrollLock` above for why this has to happen here, on the raw
  // native event, rather than in `Select`'s onOpenChange.
  const handlePointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    armScrollLock();
    onPointerDown?.(event);
  };
  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (SELECT_OPEN_KEYS.has(event.key)) armScrollLock();
    onKeyDown?.(event);
  };

  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger"
      data-size={size}
      data-fill={fill}
      className={cn(
        // Kept in step with Input (./input.tsx)'s base look — border,
        // rounded-md, shadow, focus ring — so a Select reads as the same
        // form control as a text input next to it, not a bare button.
        `flex cursor-pointer items-center ${fill ? 'w-full' : 'w-fit'} justify-between gap-1.5 rounded-md border border-input bg-transparent py-2 pr-2 pl-2.5 text-sm whitespace-nowrap shadow-(--shadow-xs) outline-none transition-[color,box-shadow] disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 data-placeholder:text-muted-foreground data-[size=default]:h-9 data-[size=sm]:h-8 *:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-1.5 focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 dark:bg-input/30 dark:hover:bg-input/50 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4`,
        className,
      )}
      onPointerDown={handlePointerDown}
      onKeyDown={handleKeyDown}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <ChevronDownIcon className="pointer-events-none size-4 text-muted-foreground" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
}

// Radix's SelectContent focuses a SelectItem — the previously-selected one
// when the panel opens (`focusSelectedItem`), and again on every arrow-key /
// hover move between options while it's open (roving tabindex) — via
// `element.scrollIntoView({ block: 'nearest' })` then `element.focus()`,
// neither with a `preventScroll` guard. When the trigger sits below the
// fold, or the user arrows through options, this is what reads as "the page
// jumps to the top" — position="popper" on SelectContent (below) only fixes
// a *different* known cause (item-aligned measuring against document
// scroll). See `suppressScrollAdjustments` (./lib) for the shared fix,
// applied here for the Select's entire open→close lifecycle via the root's
// onOpenChange below.

function SelectContent({
  className,
  children,
  // 'item-aligned' (Radix's default) measures where the selected item would
  // land and, if it doesn't fit, scrolls the whole document to make room —
  // that's what read as "the page jumps up" when opening a Select anywhere
  // but near the top of the viewport. 'popper' anchors the content to the
  // trigger via floating-ui instead, so it never touches page scroll.
  position = 'popper',
  align = 'center',
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Content>) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        data-slot="select-content"
        data-align-trigger={position === 'item-aligned'}
        className={cn(
          'relative z-50 max-h-(--radix-select-content-available-height) min-w-36 origin-(--radix-select-content-transform-origin) overflow-x-hidden overflow-y-auto rounded-xl bg-popover text-popover-foreground shadow-[var(--shadow-md)] ring-1 ring-foreground/10 duration-100 data-[align-trigger=true]:animate-none data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95',
          position === 'popper' &&
            'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
          className,
        )}
        position={position}
        align={align}
        {...props}
      >
        <SelectScrollUpButton />
        <SelectPrimitive.Viewport
          data-position={position}
          className={cn(
            'data-[position=popper]:h-(--radix-select-trigger-height) data-[position=popper]:w-full data-[position=popper]:min-w-(--radix-select-trigger-width)',
            position === 'popper' && '',
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
        <SelectScrollDownButton />
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
}

function SelectLabel({ className, ...props }: React.ComponentProps<typeof SelectPrimitive.Label>) {
  return (
    <SelectPrimitive.Label
      data-slot="select-label"
      className={cn('px-2 py-1.5 text-xs text-muted-foreground', className)}
      {...props}
    />
  );
}

function SelectItem({
  className,
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Item>) {
  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      className={cn(
        "relative flex w-full cursor-pointer items-center gap-2 rounded-md py-1.5 pr-8 pl-2 text-sm outline-hidden select-none focus:bg-accent focus:text-accent-foreground not-data-[variant=destructive]:focus:**:text-accent-foreground data-disabled:pointer-events-none data-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2",
        className,
      )}
      {...props}
    >
      <span className="pointer-events-none absolute right-2 flex size-4 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <CheckIcon className="pointer-events-none" />
        </SelectPrimitive.ItemIndicator>
      </span>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
}

function SelectSeparator({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Separator>) {
  return (
    <SelectPrimitive.Separator
      data-slot="select-separator"
      className={cn('pointer-events-none -mx-1 my-1 h-px bg-border', className)}
      {...props}
    />
  );
}

function SelectScrollUpButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollUpButton>) {
  return (
    <SelectPrimitive.ScrollUpButton
      data-slot="select-scroll-up-button"
      className={cn(
        "z-10 flex cursor-pointer items-center justify-center bg-popover py-1 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    >
      <ChevronUpIcon />
    </SelectPrimitive.ScrollUpButton>
  );
}

function SelectScrollDownButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollDownButton>) {
  return (
    <SelectPrimitive.ScrollDownButton
      data-slot="select-scroll-down-button"
      className={cn(
        "z-10 flex cursor-pointer items-center justify-center bg-popover py-1 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    >
      <ChevronDownIcon />
    </SelectPrimitive.ScrollDownButton>
  );
}

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
};
